// ═══════════════════════════════════════════════════════════════
// DEBRIS ADMS — Zones CRUD
// Table with status filter, modal for create/edit
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Select,
  Textarea, ActionIcon, Modal, Stack, SimpleGrid, MultiSelect, Popover, Tooltip,
} from '@mantine/core';
import {
  IconSearch, IconPlus, IconPencil, IconTrash, IconRefresh, IconDownload,
  IconCheck, IconChevronRight, IconChevronLeft,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useDebrisStore } from '../../store/debrisStore';
import { useAuthStore } from '../../store/authStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import { useIncidentZones } from '../../hooks/useIncidentZones';
import { ZONE_TYPE_META } from '../../store/geoopsStore';
import { geometryCentroid } from '../../lib/geoopsGeo';
import { DEBRIS_TYPE_LABELS } from '../../types/debris';
import type { DebrisZone, DebrisType, ZoneStatus } from '../../types/debris';
import MapPicker from '../impact/components/MapPicker';

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'active', label: 'Active' },
  { value: 'completed', label: 'Completed' },
  { value: 'closed', label: 'Closed' },
];

const DEBRIS_TYPE_OPTIONS = Object.entries(DEBRIS_TYPE_LABELS).map(([v, l]) => ({ value: v, label: l }));

const STATUS_COLORS: Record<string, string> = {
  pending: 'yellow', active: 'green', completed: 'blue', closed: 'gray',
};

const PASS_COLORS: Record<string, string> = {
  '1': 'blue', '2': 'orange', '3': 'violet', 'final': 'green',
};
const PASS_LABELS: Record<string, string> = {
  '1': 'Pass 1', '2': 'Pass 2', '3': 'Pass 3', 'final': 'Final',
};
const PASS_ORDER = ['1', '2', '3', 'final'] as const;
const nextPass = (current: string): string | null => {
  const idx = PASS_ORDER.indexOf(current as typeof PASS_ORDER[number]);
  return idx >= 0 && idx < PASS_ORDER.length - 1 ? PASS_ORDER[idx + 1] : null;
};
const prevPass = (current: string): string | null => {
  const idx = PASS_ORDER.indexOf(current as typeof PASS_ORDER[number]);
  return idx > 0 ? PASS_ORDER[idx - 1] : null;
};

const EMPTY: Partial<DebrisZone> = {
  zoneName: '', zoneCode: '', status: 'pending', boundaryGeojson: '',
  centerLat: '', centerLng: '', estimatedCubicYards: '', actualCubicYards: '',
  debrisTypes: '[]', assignedContractorId: '', notes: '',
};

export default function Zones() {
  const { activeIncident } = useAuthStore();
  const { user } = useAuthStore();
  const { canEdit } = useModulePermission('debris');
  const incidentId = activeIncident?.id?.toString() ?? '';

  const {
    zones, contractors, loadingZones,
    loadZones, loadContractors,
    createZone, updateZone, deleteZone,
  } = useDebrisStore();

  // GeoOps import
  const { zones: geoopsZones, isLoading: geoopsLoading } = useIncidentZones();
  const [importOpen, setImportOpen] = useState(false);
  const [importSelected, setImportSelected] = useState<Set<number>>(new Set());
  const [importing, setImporting] = useState(false);

  const [advancePopover, setAdvancePopover] = useState<number | null>(null);
  const [advancing, setAdvancing] = useState(false);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<DebrisZone | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DebrisZone | null>(null);
  const [form, setForm] = useState<Partial<DebrisZone>>({ ...EMPTY });
  const [formTypes, setFormTypes] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadZones(); loadContractors(); }, [loadZones, loadContractors]);

  const iZones = incidentId ? zones : [];
  const iContractors = incidentId ? contractors : [];

  const contractorSelect = useMemo(() =>
    iContractors.map(c => ({ value: String(c.id), label: c.companyName || `#${c.id}` })),
  [iContractors]);

  const filtered = useMemo(() => {
    let list = iZones;
    if (statusFilter && statusFilter !== 'all') list = list.filter(z => z.status === statusFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(z =>
        z.zoneName.toLowerCase().includes(q) || z.zoneCode.toLowerCase().includes(q),
      );
    }
    return [...list].sort((a, b) => a.zoneName.localeCompare(b.zoneName));
  }, [iZones, statusFilter, search]);

  const contractorName = (id: string) => contractors.find(c => String(c.id) === id)?.companyName ?? id;
  const setField = (key: keyof DebrisZone, value: string) => setForm(prev => ({ ...prev, [key]: value }));

  const openCreate = () => {
    setEditTarget(null);
    setForm({ ...EMPTY });
    setFormTypes([]);
    setModalOpen(true);
  };

  const openEdit = (zone: DebrisZone) => {
    setEditTarget(zone);
    setForm({ ...zone });
    try { setFormTypes(JSON.parse(zone.debrisTypes || '[]')); } catch { setFormTypes([]); }
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.zoneName?.trim()) {
      notifications.show({ message: 'Zone name is required', color: 'yellow' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        incidentId,
        debrisTypes: JSON.stringify(formTypes),
        creatorEmail: user?.email ?? '',
      };
      if (editTarget) {
        await updateZone(editTarget.id, payload);
        notifications.show({ message: 'Zone updated', color: 'green' });
      } else {
        await createZone(payload);
        notifications.show({ message: 'Zone created', color: 'green' });
      }
      setModalOpen(false);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Save failed', color: 'red' });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await deleteZone(deleteTarget.id);
      notifications.show({ message: `${deleteTarget.zoneName} deleted`, color: 'gray' });
      setDeleteTarget(null);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Delete failed', color: 'red' });
    } finally { setSaving(false); }
  };

  const handleAdvancePass = async (zone: DebrisZone) => {
    const current = zone.currentPass || '1';
    const next = nextPass(current);
    if (!next) return;
    setAdvancing(true);
    try {
      const now = new Date().toISOString();
      let history: Array<{ pass: string; startedAt?: string; completedAt?: string; notes?: string }> = [];
      try { history = JSON.parse(zone.passHistory || '[]'); } catch { /* skip */ }
      // Complete current pass entry
      const currentEntry = history.find(h => h.pass === current && !h.completedAt);
      if (currentEntry) {
        currentEntry.completedAt = now;
      } else {
        history.push({ pass: current, completedAt: now });
      }
      // Start new pass entry
      history.push({ pass: next, startedAt: now });
      await updateZone(zone.id, {
        currentPass: next,
        passHistory: JSON.stringify(history),
      });
      notifications.show({
        message: `${zone.zoneName} advanced to ${PASS_LABELS[next]}`,
        color: PASS_COLORS[next] ?? 'gray',
      });
      setAdvancePopover(null);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Failed to advance pass', color: 'red' });
    } finally { setAdvancing(false); }
  };

  const handleRevertPass = async (zone: DebrisZone) => {
    const current = zone.currentPass || '1';
    const prev = prevPass(current);
    if (!prev) return;
    setAdvancing(true);
    try {
      const now = new Date().toISOString();
      let history: Array<{ pass: string; startedAt?: string; completedAt?: string; notes?: string }> = [];
      try { history = JSON.parse(zone.passHistory || '[]'); } catch { /* skip */ }
      history.push({ pass: current, completedAt: now, notes: `Reverted to ${PASS_LABELS[prev]}` });
      history.push({ pass: prev, startedAt: now, notes: 'Restarted after revert' });
      await updateZone(zone.id, { currentPass: prev, passHistory: JSON.stringify(history) });
      notifications.show({ message: `${zone.zoneName} reverted to ${PASS_LABELS[prev]}`, color: 'yellow' });
      setAdvancePopover(null);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Failed to revert pass', color: 'red' });
    } finally { setAdvancing(false); }
  };

  // Pass progress stats
  const passStats = useMemo(() => {
    const counts: Record<string, number> = { '1': 0, '2': 0, '3': 0, 'final': 0 };
    for (const z of iZones) {
      const p = z.currentPass || '1';
      if (p in counts) counts[p]++;
      else counts['1']++;
    }
    return counts;
  }, [iZones]);

  // Filter GeoOps zones to only those not already imported (by name match)
  const importableZones = useMemo(() => {
    const existingNames = new Set(iZones.map(z => z.zoneName.toLowerCase().trim()));
    return geoopsZones.filter(gz => !existingNames.has(gz.name.toLowerCase().trim()));
  }, [geoopsZones, iZones]);

  const handleImport = async () => {
    if (importSelected.size === 0) return;
    setImporting(true);
    try {
      let count = 0;
      for (const gzId of importSelected) {
        const gz = geoopsZones.find(z => z.id === gzId);
        if (!gz) continue;
        const center = gz.geometry ? geometryCentroid(gz.geometry) : null;
        await createZone({
          incidentId,
          zoneName: gz.name,
          zoneCode: gz.zoneType?.toUpperCase().replace(/_/g, '-').slice(0, 3) + '-' + String(gz.id).slice(-3),
          status: gz.status === 'active' ? 'active' : 'pending',
          boundaryGeojson: gz.geometry ? JSON.stringify(gz.geometry) : '',
          centerLat: center ? String(center[0]) : '',
          centerLng: center ? String(center[1]) : '',
          estimatedCubicYards: '',
          actualCubicYards: '',
          debrisTypes: '[]',
          assignedContractorId: '',
          notes: `Imported from GeoOps ${ZONE_TYPE_META[gz.zoneType]?.label || gz.zoneType} zone`,
          creatorEmail: user?.email ?? '',
        });
        count++;
      }
      notifications.show({ message: `Imported ${count} zone${count !== 1 ? 's' : ''} from GeoOps`, color: 'green' });
      setImportOpen(false);
      setImportSelected(new Set());
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Import failed', color: 'red' });
    } finally { setImporting(false); }
  };

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Debris Zones</Title>
        <Group gap="xs">
          <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />}
            loading={loadingZones} onClick={() => loadZones()}>Refresh</Button>
          {canEdit && geoopsZones.length > 0 && (
            <Button size="xs" variant="light" color="teal" leftSection={<IconDownload size={14} />}
              onClick={() => { setImportSelected(new Set()); setImportOpen(true); }}
              loading={geoopsLoading}>
              Import from GeoOps {importableZones.length > 0 ? `(${importableZones.length})` : ''}
            </Button>
          )}
          {canEdit && <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>Add Zone</Button>}
        </Group>
      </Group>

      <Group mb="md" gap="sm">
        <TextInput placeholder="Search zone name or code..."
          leftSection={<IconSearch size={14} />} value={search}
          onChange={e => setSearch(e.currentTarget.value)} size="xs"
          style={{ flex: 1, minWidth: 200 }} />
        <Select data={[{ value: 'all', label: 'All Statuses' }, ...STATUS_OPTIONS]}
          value={statusFilter ?? 'all'} onChange={v => setStatusFilter(v)} size="xs" w={160} />
      </Group>

      <Text size="xs" c="dimmed" mb="xs">{filtered.length} zone{filtered.length !== 1 ? 's' : ''}</Text>

      {iZones.length > 0 && (
        <Group gap="sm" mb="sm">
          {PASS_ORDER.map(p => (
            <Badge key={p} size="sm" variant="light" color={PASS_COLORS[p]}>
              {PASS_LABELS[p]}: {passStats[p]}
            </Badge>
          ))}
        </Group>
      )}

      <Paper withBorder>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--mantine-color-gray-3)' }}>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Zone</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Code</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Status</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Pass</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Debris Types</th>
                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Est. CY</th>
                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Actual CY</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Contractor</th>
                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={9} style={{ padding: 24, textAlign: 'center', color: '#888' }}>
                  {loadingZones ? 'Loading...' : 'No zones found'}
                </td></tr>
              )}
              {filtered.map(zone => {
                let types: string[] = [];
                try { types = JSON.parse(zone.debrisTypes || '[]'); } catch { /* skip */ }
                return (
                  <tr key={zone.id} style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}>
                    <td style={{ padding: '8px 12px', fontWeight: 500 }}>{zone.zoneName || '-'}</td>
                    <td style={{ padding: '8px 12px' }}>{zone.zoneCode || '-'}</td>
                    <td style={{ padding: '8px 12px' }}>
                      <Badge size="sm" color={STATUS_COLORS[zone.status] ?? 'gray'}>{zone.status}</Badge>
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <Group gap={4} wrap="nowrap">
                        <Badge size="sm" color={PASS_COLORS[zone.currentPass || '1'] ?? 'blue'}>
                          {PASS_LABELS[zone.currentPass || '1'] ?? 'Pass 1'}
                        </Badge>
                        {canEdit && (prevPass(zone.currentPass || '1') || nextPass(zone.currentPass || '1')) && (
                          <Popover opened={advancePopover === zone.id} onClose={() => setAdvancePopover(null)} position="bottom" withArrow>
                            <Popover.Target>
                              <Group gap={2}>
                                {prevPass(zone.currentPass || '1') && (
                                  <Tooltip label={`Revert to ${PASS_LABELS[prevPass(zone.currentPass || '1')!]}`} withArrow>
                                    <ActionIcon size="xs" variant="subtle" color="gray"
                                      onClick={() => setAdvancePopover(advancePopover === zone.id ? null : zone.id)}>
                                      <IconChevronLeft size={12} />
                                    </ActionIcon>
                                  </Tooltip>
                                )}
                                {nextPass(zone.currentPass || '1') && (
                                  <Tooltip label={`Advance to ${PASS_LABELS[nextPass(zone.currentPass || '1')!]}`} withArrow>
                                    <ActionIcon size="xs" variant="subtle" color={PASS_COLORS[nextPass(zone.currentPass || '1')!]}
                                      onClick={() => setAdvancePopover(advancePopover === zone.id ? null : zone.id)}>
                                      <IconChevronRight size={12} />
                                    </ActionIcon>
                                  </Tooltip>
                                )}
                              </Group>
                            </Popover.Target>
                            <Popover.Dropdown>
                              <Stack gap="xs">
                                <Text size="xs" fw={600}>{zone.zoneName} — currently on {PASS_LABELS[zone.currentPass || '1']}</Text>
                                <Group gap="xs" justify="flex-end">
                                  <Button size="compact-xs" variant="default" onClick={() => setAdvancePopover(null)}>Cancel</Button>
                                  {prevPass(zone.currentPass || '1') && (
                                    <Button size="compact-xs" color="gray" variant="light"
                                      loading={advancing} onClick={() => handleRevertPass(zone)}>
                                      ← {PASS_LABELS[prevPass(zone.currentPass || '1')!]}
                                    </Button>
                                  )}
                                  {nextPass(zone.currentPass || '1') && (
                                    <Button size="compact-xs" color={PASS_COLORS[nextPass(zone.currentPass || '1')!]}
                                      loading={advancing} onClick={() => handleAdvancePass(zone)}>
                                      {PASS_LABELS[nextPass(zone.currentPass || '1')!]} →
                                    </Button>
                                  )}
                                </Group>
                              </Stack>
                            </Popover.Dropdown>
                          </Popover>
                        )}
                      </Group>
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <Group gap={4}>
                        {types.slice(0, 3).map(t => (
                          <Badge key={t} size="xs" variant="light">
                            {DEBRIS_TYPE_LABELS[t as DebrisType] ?? t}
                          </Badge>
                        ))}
                        {types.length > 3 && <Badge size="xs" variant="light" color="gray">+{types.length - 3}</Badge>}
                      </Group>
                    </td>
                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                      {parseFloat(zone.estimatedCubicYards || '0').toLocaleString()}
                    </td>
                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                      {parseFloat(zone.actualCubicYards || '0').toLocaleString()}
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      {zone.assignedContractorId ? contractorName(zone.assignedContractorId) : '-'}
                    </td>
                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                      <Group gap={4} justify="flex-end">
                        {canEdit && <ActionIcon size="sm" variant="subtle" onClick={() => openEdit(zone)}>
                          <IconPencil size={14} />
                        </ActionIcon>}
                        {canEdit && <ActionIcon size="sm" variant="subtle" color="red" onClick={() => setDeleteTarget(zone)}>
                          <IconTrash size={14} />
                        </ActionIcon>}
                      </Group>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Paper>

      {/* Create / Edit Modal */}
      <Modal opened={modalOpen} onClose={() => setModalOpen(false)}
        title={editTarget ? 'Edit Zone' : 'Add Zone'} centered size="lg">
        <Stack>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <TextInput label="Zone Name" required value={form.zoneName ?? ''}
              onChange={e => setField('zoneName', e.currentTarget.value)} />
            <TextInput label="Zone Code" value={form.zoneCode ?? ''}
              onChange={e => setField('zoneCode', e.currentTarget.value)} />
            <Select label="Status" data={STATUS_OPTIONS}
              value={(form.status as ZoneStatus) ?? 'pending'}
              onChange={v => setField('status', v ?? 'pending')} />
            <Select label="Assigned Contractor" data={contractorSelect}
              value={form.assignedContractorId || null}
              onChange={v => setField('assignedContractorId', v ?? '')} searchable clearable />
            <TextInput label="Estimated Cubic Yards" value={form.estimatedCubicYards ?? ''}
              onChange={e => setField('estimatedCubicYards', e.currentTarget.value)} />
            <TextInput label="Actual Cubic Yards" value={form.actualCubicYards ?? ''}
              onChange={e => setField('actualCubicYards', e.currentTarget.value)} />
            {editTarget ? (
              <div>
                <Text size="sm" fw={500} mb={4}>Current Pass</Text>
                <Badge size="lg" color={PASS_COLORS[form.currentPass || '1'] ?? 'blue'}>
                  {PASS_LABELS[form.currentPass || '1'] ?? 'Pass 1'}
                </Badge>
              </div>
            ) : (
              <Select label="Initial Pass" data={PASS_ORDER.map(p => ({ value: p, label: PASS_LABELS[p] }))}
                value={form.currentPass || '1'}
                onChange={v => setField('currentPass', v ?? '1')} />
            )}
          </SimpleGrid>
          <Text size="sm" fw={600} mt="xs">Zone Location</Text>
          <Text size="xs" c="dimmed">Click on the map to set the zone center point</Text>
          <MapPicker
            value={form.centerLat && form.centerLng ? { lat: parseFloat(form.centerLat), lng: parseFloat(form.centerLng) } : null}
            onChange={pos => {
              setForm(prev => ({ ...prev, centerLat: String(pos.lat), centerLng: String(pos.lng) }));
            }}
            height={250}
          />
          {form.centerLat && form.centerLng && (
            <Group gap="sm">
              <TextInput label="Latitude" size="xs" value={form.centerLat} readOnly style={{ flex: 1 }} />
              <TextInput label="Longitude" size="xs" value={form.centerLng} readOnly style={{ flex: 1 }} />
            </Group>
          )}
          <MultiSelect label="Debris Types" data={DEBRIS_TYPE_OPTIONS}
            value={formTypes} onChange={setFormTypes} searchable />
          <Textarea label="Notes" value={form.notes ?? ''}
            onChange={e => setField('notes', e.currentTarget.value)} autosize minRows={2} />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button loading={saving} onClick={handleSave}>
              {editTarget ? 'Save Changes' : 'Create Zone'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Delete confirmation */}
      <Modal opened={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Delete Zone" centered size="sm">
        <Stack>
          <Text size="sm">Delete <strong>{deleteTarget?.zoneName}</strong>? This cannot be undone.</Text>
          <Group justify="flex-end">
            <Button variant="default" size="xs" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button color="red" size="xs" loading={saving} onClick={handleDelete}>Delete</Button>
          </Group>
        </Stack>
      </Modal>

      {/* Import from GeoOps modal */}
      <Modal opened={importOpen} onClose={() => setImportOpen(false)}
        title="Import Zones from GeoOps" centered size="lg">
        <Stack gap="sm">
          {importableZones.length === 0 ? (
            <Text c="dimmed" ta="center" py="lg">
              {geoopsZones.length === 0 ? 'No GeoOps zones found for this incident. Draw zones in GeoOps first.'
                : 'All GeoOps zones have already been imported.'}
            </Text>
          ) : (
            <>
              <Text size="sm" c="dimmed">
                Select GeoOps zones to import as debris work zones. Already-imported zones are hidden.
              </Text>
              <Group gap={4}>
                <Button size="compact-xs" variant="subtle"
                  onClick={() => setImportSelected(new Set(importableZones.map(z => z.id)))}>
                  Select All
                </Button>
                <Button size="compact-xs" variant="subtle"
                  onClick={() => setImportSelected(new Set())}>
                  Clear
                </Button>
              </Group>
              <Stack gap={4}>
                {importableZones.map(gz => {
                  const meta = ZONE_TYPE_META[gz.zoneType];
                  const selected = importSelected.has(gz.id);
                  return (
                    <Paper key={gz.id} p="sm" radius="sm" withBorder
                      style={{
                        cursor: 'pointer',
                        background: selected ? 'var(--mantine-color-teal-0)' : undefined,
                        borderColor: selected ? 'var(--mantine-color-teal-4)' : undefined,
                      }}
                      onClick={() => {
                        const next = new Set(importSelected);
                        if (selected) next.delete(gz.id); else next.add(gz.id);
                        setImportSelected(next);
                      }}>
                      <Group justify="space-between">
                        <Group gap={8}>
                          {selected && <IconCheck size={14} color="teal" />}
                          <div>
                            <Text size="sm" fw={500}>{gz.name}</Text>
                            <Group gap={6}>
                              <Badge size="xs" color={meta?.mantine || 'gray'}>{meta?.label || gz.zoneType}</Badge>
                              <Badge size="xs" variant="light">{gz.status}</Badge>
                            </Group>
                          </div>
                        </Group>
                        {gz.notes && <Text size="xs" c="dimmed" maw={200} lineClamp={1}>{gz.notes}</Text>}
                      </Group>
                    </Paper>
                  );
                })}
              </Stack>
            </>
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setImportOpen(false)}>Cancel</Button>
            <Button color="teal" loading={importing} onClick={handleImport}
              disabled={importSelected.size === 0}>
              Import {importSelected.size} Zone{importSelected.size !== 1 ? 's' : ''}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
}
